import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useFont } from '../contexts/FontContext';
import { AcademicCapIcon, LockClosedIcon, UserIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { User } from '../types';
import { getApiUrl } from '../config';
import { debugApiConnection } from '../utils/apiDebug';

interface LoginFormData {
  identifier: string; // Can be email, userID, or phone
  password: string;
}

const Login: React.FC = () => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginFormData>();
  const { login, updateUser } = useAuth();
  const { regularClass, boldClass } = useFont();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [loginMethod, setLoginMethod] = useState<'email' | 'userid' | 'phone'>('email');

  // Check API status on component mount
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/health`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          setApiStatus('online');
        } else {
          setApiStatus('offline');
        }
      } catch (error) {
        console.error('API health check failed:', error);
        setApiStatus('offline');
      }
    };

    checkApiStatus();
  }, []);

  const fillAdminCredentials = () => {
    setValue('identifier', 'aitthisonedev@gmail.com');
    setValue('password', '1q2w3e4r');
    setLoginMethod('email');
  };

  const fillStudentCredentials = () => {
    setValue('identifier', 'it@aitthisone.com');
    setValue('password', '1q2w3e4r');
    setLoginMethod('email');
  };

  const validateIdentifier = (value: string) => {
    if (!value) return 'ຕ້ອງການອີເມວ, ລະຫັດຜູ້ໃຊ້, ຫຼື ເບີໂທລະສັບ';
    
    switch (loginMethod) {
      case 'email':
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return emailRegex.test(value) || 'ທີ່ຢູ່ອີເມວບໍ່ຖືກຕ້ອງ';
      case 'phone':
        const phoneRegex = /^[+]?[\d\s\-\(\)]{10,20}$/;
        return phoneRegex.test(value) || 'ຮູບແບບເບີໂທລະສັບບໍ່ຖືກຕ້ອງ';
      case 'userid':
        return value.length >= 3 || 'ລະຫັດຜູ້ໃຊ້ຕ້ອງມີຢ່າງໜ້ອຍ 3 ຕົວອັກສອນ';
      default:
        return true;
    }
  };

  const getIdentifierIcon = () => {
    switch (loginMethod) {
      case 'email':
        return <EnvelopeIcon className="h-5 w-5 text-gray-400" />;
      case 'phone':
        return <PhoneIcon className="h-5 w-5 text-gray-400" />;
      case 'userid':
        return <UserIcon className="h-5 w-5 text-gray-400" />;
      default:
        return <UserIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getIdentifierPlaceholder = () => {
    switch (loginMethod) {
      case 'email':
        return 'ອີເມວ';
      case 'phone':
        return '+85620xxxxxxx';
      case 'userid':
        return 'STD-xxxx';
      default:
        return 'ອີເມວ, ລະຫັດຜູ້ໃຊ້, ຫຼື ເບີໂທລະສັບ';
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    console.log('Attempting login with:', data.identifier, 'Method:', loginMethod);

    try {
      // Use test credentials for admin
      const identifier = data.identifier === 'admin' ? 'admin@example.com' : data.identifier;
      const password = data.password === 'admin' ? 'admin123' : data.password;

      // Use test credentials for student
      const processedIdentifier = identifier === 'student' ? 'student@example.com' : identifier;
      const processedPassword = password === 'student' ? 'student123' : password;

      console.log('Processed credentials:', { identifier: processedIdentifier });
      
      try {
        // Try online login first
        const response = await login(processedIdentifier, processedPassword, loginMethod);
        
        // Get user role from the response
        const userRole = response?.user?.role;
        
        console.log('Login successful:', response);
        console.log('User role:', userRole);
        
        // Redirect based on role with a slight delay to ensure state is updated
        setTimeout(() => {
          if (userRole === 'admin') {
            console.log('Redirecting to admin analytics');
            navigate('/admin/analytics', { replace: true });
          } else {
            console.log('Redirecting to student dashboard');
            navigate('/dashboard', { replace: true });
          }
        }, 100);
      } catch (apiError: any) {
        console.error('API error during login:', apiError);
        
        // If no connection to backend, allow offline login with test accounts
        if (!apiError.response && (processedIdentifier === 'admin@example.com' || processedIdentifier === 'student@example.com')) {
          console.log('No backend connection, using offline mode login');
          setIsOfflineMode(true);
          
          // Create mock user data
          const mockUser: User = {
            id: processedIdentifier === 'admin@example.com' ? 1 : 2,
            name: processedIdentifier === 'admin@example.com' ? 'Admin User' : 'Student User',
            email: processedIdentifier,
            role: processedIdentifier === 'admin@example.com' ? 'admin' : 'student',
            status: 'approved'
          };
          
          // Update user state with mock data
          updateUser(mockUser);
          
          // Store mock tokens
          localStorage.setItem('token', 'mock-token-for-offline-mode');
          localStorage.setItem('refreshToken', 'mock-refresh-token-for-offline-mode');
          
          console.log('Offline mode login successful:', mockUser);
          
          // Redirect based on role
          setTimeout(() => {
            if (mockUser.role === 'admin') {
              console.log('Redirecting to admin analytics (offline mode)');
              navigate('/admin/analytics', { replace: true });
            } else {
              console.log('Redirecting to student dashboard (offline mode)');
              navigate('/dashboard', { replace: true });
            }
          }, 100);
          
          return;
        }
        
        // Handle regular API errors
        let errorMsg = 'ການເຂົ້າສູ່ລະບົບລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.';
        
        if (apiError.response) {
          // Server responded with an error
          if (apiError.response.status === 401) {
            errorMsg = 'ອີເມວ, ລະຫັດຜູ້ໃຊ້, ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ. ກະລຸນາລອງໃໝ່.';
          } else if (apiError.response.status === 403) {
            errorMsg = 'ບັນຊີຂອງທ່ານຍັງບໍ່ໄດ້ອະນຸມັດເທື່ອ. ກະລຸນາລໍຖ້າການອະນຸມັດຂອງຜູ້ເບິ່ງແຍງ ຫຼື ຕິດຕໍ່ WhatsApp ເພື່ອຮັບການອະນຸມັດ';
          } else if (apiError.response.status === 404) {
            errorMsg = 'ບໍ່ພົບຜູ້ໃຊ້. ກະລຸນາກວດສອບຂໍ້ມູນຂອງທ່ານ.';
          } else {
            errorMsg = apiError.response.data?.message || errorMsg;
          }
          console.log('Server error response:', apiError.response.data);
        } else if (apiError.request) {
          // Request was made but no response received
          errorMsg = 'ບໍ່ມີການຕອບສະໜອງຈາກເຊີເວີ. ກະລຸນາກວດສອບການເຊື່ອມຕໍ່ຂອງທ່ານ.';
          console.log('No response from server:', apiError.request);
        } else {
          // Something else happened
          console.log('Error setting up request:', apiError.message);
        }
        
        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError('ເກີດຂໍ້ຜິດພາດທີ່ບໍ່ຄາດຄິດ. ກະລຸນາລອງໃໝ່.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6 xl:px-8 ${regularClass}`}>
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 border border-indigo-100">
          <div className="flex justify-start">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-2 sm:p-3 shadow-lg">
              <AcademicCapIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
            </div>
          <h2 className="mt-4 sm:mt-6 ml-3 sm:ml-4 text-start text-2xl sm:text-3xl font-extrabold text-gray-900">
            ຍິນດີຕ້ອນຮັບ!
          </h2>
          </div>
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
            <Link 
              to="/" 
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors font-medium text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              ກັບຄືນ
            </Link>
            
            <Link 
              to="/register" 
              className="px-3 sm:px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg shadow-md hover:from-indigo-600 hover:to-purple-600 transition-all transform hover:scale-105 font-medium text-sm flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6z" />
                <path d="M16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
              ສະໝັກສະມາຊິກໃໝ່
            </Link>
          </div>
        
          <form className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {apiStatus === 'checking' && (
              <div className="rounded-lg bg-blue-50 p-3 sm:p-4 border border-blue-200">
                <div className="text-xs sm:text-sm text-blue-800">ກຳລັງກວດສອບການເຊື່ອມຕໍ່ເຊີເວີ...</div>
              </div>
            )}
            
            {apiStatus === 'offline' && (
              <div className="rounded-lg bg-yellow-50 p-3 sm:p-4 border border-yellow-200">
                <div className="text-xs sm:text-sm text-yellow-800">
                  ເຊີເວີອອບໄລນ໌. ກຳລັງໃຊ້ໂໝດສາທິດທີ່ມີຄວາມສາມາດຈຳກັດ.
                </div>
              </div>
            )}
            
            {error && (
              <div className="rounded-lg bg-red-50 p-3 sm:p-4 border border-red-200">
                <div className="text-xs sm:text-sm text-red-800">{error}</div>
              </div>
            )}
            
            {isOfflineMode && (
              <div className="rounded-lg bg-yellow-50 p-3 sm:p-4 border border-yellow-200">
                <div className="text-xs sm:text-sm text-yellow-800">
                  ກຳລັງໃຊ້ໂໝດອອບໄລນ໌. ບາງຄຸນສົມບັດອາດຈະຈຳກັດ.
                </div>
              </div>
            )}

            <div className="space-y-3 sm:space-y-4">
              {/* Login Method Selector */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                ເຂົ້າສູ່ລະບົບດ້ວຍ
                </label>
                <div className="grid grid-cols-3 gap-1 sm:gap-2">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('email')}
                    className={`py-2 px-2 sm:px-3 text-xs font-medium rounded-lg transition-colors ${
                      loginMethod === 'email'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ອີເມວ
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMethod('userid')}
                    className={`py-2 px-2 sm:px-3 text-xs font-medium rounded-lg transition-colors ${
                      loginMethod === 'userid'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ລະຫັດຜູ້ໃຊ້
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoginMethod('phone')}
                    className={`py-2 px-2 sm:px-3 text-xs font-medium rounded-lg transition-colors ${
                      loginMethod === 'phone'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ເບີໂທ
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="identifier" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  {loginMethod === 'email' ? 'ທີ່ຢູ່ອີເມວ' : 
                   loginMethod === 'phone' ? 'ເບີໂທລະສັບ' : 'ລະຫັດຜູ້ໃຊ້'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {getIdentifierIcon()}
                  </div>
                  <input
                    {...register('identifier', {
                      required: 'ຕ້ອງການຂໍ້ມູນ',
                      validate: validateIdentifier,
                    })}
                    type={loginMethod === 'phone' ? 'tel' : 'text'}
                    autoComplete={loginMethod === 'email' ? 'email' : loginMethod === 'phone' ? 'tel' : 'username'}
                    className="appearance-none block w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm"
                    placeholder={getIdentifierPlaceholder()}
                  />
                </div>
                {errors.identifier && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.identifier.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ລະຫັດຜ່ານ
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('password', { required: 'ຕ້ອງການລະຫັດຜ່ານ' })}
                    type="password"
                    autoComplete="current-password"
                    className="appearance-none block w-full pl-10 pr-3 py-2.5 sm:py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 text-sm"
                    placeholder="ລະຫັດຜ່ານ"
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors">
                ລືມລະຫັດຜ່ານ?
              </Link>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all hover:scale-[1.02] shadow-lg"
              >
                {isLoading ? (
                  <>
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-indigo-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    ກຳລັງເຂົ້າສູ່ລະບົບ...
                  </>
                ) : 'ເຂົ້າສູ່ລະບົບ'}
              </button>
            </div>

            {/* <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
              <p className="text-center text-sm font-medium text-indigo-700 mb-2">
                ຂໍ້ມູນສຳລັບທົດສອບ (ຄລິກ):
              </p>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div 
                  onClick={fillAdminCredentials}
                  className="bg-white rounded-md p-2 shadow-sm cursor-pointer hover:bg-indigo-100 transition-colors border border-transparent hover:border-indigo-300 transform hover:scale-[1.02]"
                  title="ຄລິກເພື່ອຕື່ມຂໍ້ມູນຜູ້ບໍລິຫານອັດຕະໂນມັດ"
                >
                  <p className="text-xs font-medium text-indigo-700">ຜູ້ບໍລິຫານ</p>
                  <p className="text-xs font-medium text-gray-700">aitthisonedev@gmail.com</p>
                  <p className="text-xs text-gray-500">ລະຫັດຜ່ານ:1q2w3e4r</p>
                </div>
                <div 
                  onClick={fillStudentCredentials}
                  className="bg-white rounded-md p-2 shadow-sm cursor-pointer hover:bg-indigo-100 transition-colors border border-transparent hover:border-indigo-300 transform hover:scale-[1.02]"
                  title="ຄລິກເພື່ອຕື່ມຂໍ້ມູນນັກຮຽນອັດຕະໂນມັດ"
                >
                  <p className="text-xs font-medium text-indigo-700">ນັກຮຽນ</p>
                  <p className="text-xs text-gray-500">it@aitthisone.com</p>
                  <p className="text-xs text-gray-500">ລະຫັດຜ່ານ:1q2w3e4r</p>
                </div>
              </div>
            </div> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;